// src/utils/jwt.ts
export function isJwtExpired(token: string): boolean {
  try {
    const payloadBase64 = token.split(".")[1];
    const decodedPayload = JSON.parse(atob(payloadBase64));
    const exp = decodedPayload.exp;
    if (typeof exp !== "number") return true;
    const expiry = new Date(exp * 1000);
    return new Date() > expiry;
  } catch (e) {
    return true;
  }
}

export function decodeJwt(token: string): any {
  try {
    const payloadBase64 = token.split(".")[1];
    const decodedPayload = JSON.parse(atob(payloadBase64));
    return decodedPayload;
  } catch (e) {
    return null;
  }
}

export function isAdminFromToken(token: string): boolean {
  try {
    const payload = decodeJwt(token);
    return payload && payload.admin === true;
  } catch (e) {
    return false;
  }
}

export interface TokenAccessInfo {
  hasPanelAccess: boolean;
  canDeleteAnnouncements: boolean;
  canManageUsers: boolean;
  canManageUserRoles: boolean;
  canDeleteSongs: boolean;
  canDeleteSongRequests: boolean;
  roles: string[];
  claimsShape: string[];
}

function normalizeRoleList(payload: any): string[] {
  const roles = new Set<string>();
  const normalizeRole = (role: string): string => role.trim().toUpperCase();

  if (typeof payload?.role === "string") {
    roles.add(normalizeRole(payload.role));
  }

  if (Array.isArray(payload?.roles)) {
    payload.roles.forEach((role: unknown) => {
      if (typeof role === "string") {
        roles.add(normalizeRole(role));
      }
    });
  }

  if (payload?.admin === true) {
    roles.add("ADMIN");
  }

  return Array.from(roles);
}

export function getTokenAccessInfo(token: string): TokenAccessInfo {
  const payload = decodeJwt(token) || {};
  const roles = normalizeRoleList(payload);
  const hasAdminRole = roles.includes("ADMIN");
  const hasContentManagerRole = roles.includes("CONTENT_MANAGER");

  return {
    hasPanelAccess: hasAdminRole || hasContentManagerRole,
    canDeleteAnnouncements: hasAdminRole,
    canManageUsers: hasAdminRole,
    canManageUserRoles: hasAdminRole,
    canDeleteSongs: hasAdminRole,
    canDeleteSongRequests: hasAdminRole,
    roles,
    claimsShape: Object.keys(payload),
  };
}

export function getCurrentTokenAccessInfo(): TokenAccessInfo | null {
  const token = localStorage.getItem("authToken");
  if (!token) return null;
  return getTokenAccessInfo(token);
}
